import { ReloadOutlined } from '@ant-design/icons';
import { Button, Collapse, Select, Space, Spin, Table } from 'antd';
import React, { useContext, useMemo, useState } from 'react';

import StickyPageHeader from '../components/common/StickyPageHeader';
import { AuthContext } from '../contexts/authContext';
import {
  useCarrierClasses,
  useCostKmStats,
  useModes,
  useSelectedCarrier,
} from '../hooks/apiHooks';
import Layout from '../layout';
import { UserRole } from '../models/User';

const CarrierCostPerKmStatsPage = ({ disableLayout, routes }) => {
  const { authUser } = useContext(AuthContext);
  const { carrierId, selectedCarrier } = useSelectedCarrier();
  const { costKmStats, isFetching, isLoading, refetch } = useCostKmStats(carrierId);
  const { carrierClasses } = useCarrierClasses(carrierId);
  const { modes } = useModes(carrierId);
  const [filterOrgProv, setFilterOrgProv] = useState();
  const [filterDestProv, setFilterDestProv] = useState();

  const isAdmin = useMemo(
    () => [UserRole.SUPER_ADMIN, UserRole.ADMIN].includes(authUser.role),
    [authUser.role]
  );

  const { destProvinces, groupedByRoute, orgProvinces } = useMemo(() => {
    if (!costKmStats?.length) {
      return { destProvinces: [], groupedByRoute: [], orgProvinces: [] };
    }
    const byKey = {};
    const orgSet = new Set();
    const destSet = new Set();
    for (const row of costKmStats) {
      orgSet.add(row.orgProv);
      destSet.add(row.destProv);
      const routeKey = `${row.orgProv}-${row.destProv}`;
      if (!byKey[routeKey]) {
        byKey[routeKey] = {
          byMode: {},
          destProv: row.destProv,
          orgProv: row.orgProv,
        };
      }
      const modeKey = row.modeId;
      if (!byKey[routeKey].byMode[modeKey]) {
        byKey[routeKey].byMode[modeKey] = [];
      }
      byKey[routeKey].byMode[modeKey].push(row);
    }
    let routes = Object.values(byKey);
    if (filterOrgProv) {
      routes = routes.filter((r) => r.orgProv === filterOrgProv);
    }
    if (filterDestProv) {
      routes = routes.filter((r) => r.destProv === filterDestProv);
    }
    return {
      destProvinces: [...destSet].sort(),
      groupedByRoute: routes,
      orgProvinces: [...orgSet].sort(),
    };
  }, [costKmStats, filterOrgProv, filterDestProv]);

  const getModeName = (modeId) => modes?.find((m) => m.id === modeId)?.name ?? '-';
  const getClassName = (classId) => carrierClasses?.find((c) => c.id === classId)?.name ?? '-';

  const buildTableConfig = (rows) => {
    const byClass = {};
    for (const r of rows) {
      byClass[r.classId] = r;
    }
    const classIds = Object.keys(byClass).map(Number).sort((a, b) => a - b);

    const metricColumns = [
      { dataIndex: 'avg', key: 'avg', render: (value) => (value != null ? Number(value).toFixed(2) : '–'), title: 'Avg' },
      { dataIndex: 'min', key: 'min', render: (value) => (value != null ? Number(value).toFixed(2) : '–'), title: 'Min' },
      { dataIndex: 'max', key: 'max', render: (value) => (value != null ? Number(value).toFixed(2) : '–'), title: 'Max' },
      { dataIndex: 'stddev', key: 'stddev', render: (value) => (value != null ? Number(value).toFixed(2) : '–'), title: 'Std Dev' },
      { dataIndex: 'sampleCount', key: 'sampleCount', render: (value) => (value != null ? value : '–'), title: 'Count' },
    ];

    const columns = [
      { dataIndex: 'class', key: 'class', title: 'Vehicle Types', width: 300 },
      ...metricColumns,
    ];

    const dataSource = classIds.map((classId) => {
      const row = byClass[classId];
      return {
        avg: row.avg,
        class: getClassName(classId),
        key: classId,
        max: row.max,
        min: row.min,
        sampleCount: row.sampleCount,
        stddev: row.stddev,
      };
    });

    return { columns, dataSource };
  };

  const content = (
    <div style={{ paddingBottom: 32 }}>
      <StickyPageHeader
        actions={
          <Button
            ghost
            icon={<ReloadOutlined />}
            loading={isLoading || isFetching}
            type="primary"
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        }
        subtitle={selectedCarrier?.name}
        title="Cost Per KM Stats"
      />
      {!isLoading && !!costKmStats?.length && (
        <Space wrap style={{ marginBottom: 16 }}>
          <span>Filter by:</span>
          <Select
            allowClear
            optionFilterProp="label"
            options={orgProvinces.map((p) => ({ label: p, value: p }))}
            placeholder="Origin"
            style={{ minWidth: 180 }}
            value={filterOrgProv}
            onChange={setFilterOrgProv}
          />
          <Select
            allowClear
            optionFilterProp="label"
            options={destProvinces.map((p) => ({ label: p, value: p }))}
            placeholder="Destination"
            style={{ minWidth: 180 }}
            value={filterDestProv}
            onChange={setFilterDestProv}
          />
        </Space>
      )}
      {isLoading || (isFetching && !groupedByRoute.length) ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : !groupedByRoute.length ? (
        <p>No cost per KM stats available</p>
      ) : (
        groupedByRoute.map((route) => {
          const collapseItems = Object.entries(route.byMode).map(([modeId, rows]) => {
            const { columns, dataSource } = buildTableConfig(rows);
            return {
              children: (
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  size="small"
                />
              ),
              key: modeId,
              label: getModeName(Number(modeId)),
            };
          });
          return (
            <div key={`${route.orgProv}-${route.destProv}`} style={{ marginBottom: 32 }}>
              <h2>
                {route.orgProv} – {route.destProv}
              </h2>
              <Collapse
                defaultActiveKey={collapseItems.map((item) => item.key)}
                items={collapseItems}
                size="small"
              />
            </div>
          );
        })
      )}
    </div>
  );

  if (disableLayout) {
    return content;
  }

  return (
    <Layout
      disableLoading
      carrierLoading={!selectedCarrier}
      menuIndex={isAdmin ? 1 : 0}
      routes={routes}
      showCarriersNavigation={isAdmin}
    >
      {content}
    </Layout>
  );
};

export default CarrierCostPerKmStatsPage;
